// src/pages/Hub/components/CommandPalette.js
//
// ⌘K / Ctrl-K universal finder. Debounced search across every region via
// /api/ops-hub/search, grouped by region, fully keyboard-navigable (↑ ↓ Enter Esc).
// Selecting a result bubbles it up so the hub can open the detail drawer.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Loader2, CornerDownLeft } from 'lucide-react';
import { searchAll } from '../lib/opsHubApi';
import { regionMeta } from '../theme';

export default function CommandPalette({ open, onClose, onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  // focus + reset when opened
  useEffect(() => {
    if (open) {
      setQ(''); setResults([]); setActive(0);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    }
  }, [open]);

  // debounced search
  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (!term) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchAll(term);
        setResults(data.results || []);
        setActive(0);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[active]) onSelect(results[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  // group results by region, preserving a stable flat index for highlight
  const groups = useMemo(() => {
    const g = {};
    results.forEach((r) => { (g[r.region] = g[r.region] || []).push(r); });
    let idx = -1;
    return Object.entries(g).map(([region, items]) => ({
      region,
      items: items.map((it) => { idx += 1; return { ...it, _idx: idx }; }),
    }));
  }, [results]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="hub-palette-scrim"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            className="hub-palette"
            initial={{ opacity: 0, y: -24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -24, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          >
            <div className="hub-palette-field">
              {loading ? <Loader2 size={20} className="hub-spin" /> : <Search size={20} />}
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search any client, contact or phone — across all regions…"
              />
              <span className="hub-palette-esc">ESC</span>
            </div>

            <div className="hub-palette-list">
              {!q.trim() ? (
                <div className="hub-palette-hint">Start typing to find a client across Residential, Commercial &amp; Mideast.</div>
              ) : !loading && results.length === 0 ? (
                <div className="hub-palette-hint">No matches for “{q.trim()}”.</div>
              ) : (
                groups.map((grp) => {
                  const meta = regionMeta(grp.region);
                  return (
                    <div key={grp.region}>
                      <div className="hub-palette-group" style={{ color: meta.color }}>{meta.label}</div>
                      {grp.items.map((r) => (
                        <button
                          key={`${r.region}-${r.id}`}
                          className={`hub-res ${r._idx === active ? 'active' : ''}`}
                          onMouseEnter={() => setActive(r._idx)}
                          onClick={() => onSelect(r)}
                        >
                          <span className="hub-res-icon" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.short}</span>
                          <span className="hub-res-main">
                            <span className="hub-res-name">{r.name || 'Unnamed'}</span>
                            <span className="hub-res-sub">
                              {[r.sub, r.phone, [r.city, r.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || '—'}
                            </span>
                          </span>
                          <span className="hub-res-badge" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.short}</span>
                        </button>
                      ))}
                    </div>
                  );
                })
              )}
            </div>

            <div className="hub-palette-foot">
              <span><kbd>↑</kbd> <kbd>↓</kbd> navigate</span>
              <span><kbd><CornerDownLeft size={11} /></kbd> open</span>
              <span><kbd>esc</kbd> close</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
